/**
 * Error reporting, launch side (section 22). Holds no Sentry code: it keeps the errors of
 * the first seconds in a short queue and, once the page has loaded and the browser is
 * idle, imports ../sentry (its own lazy chunk, precached by the service worker so it loads
 * offline too), which starts the SDK and sends the queue. Without VITE_SENTRY_DSN at build
 * time `mulaiPelaporan` returns at once and nothing is imported.
 */
import { adalahBatasTransisi } from './batasTransisi'

interface Tertunda {
  galat: unknown
  /** React's component stack, for errors an error boundary caught. */
  komponen?: string | undefined
}

type Pelapor = (t: Tertunda) => void

const BATAS_ANTREAN = 20
const antrean: Tertunda[] = []
let pelapor: Pelapor | null = null
let aktif = false

/** Reports an error now if Sentry runs, or queues it until it does; never throws. */
export function laporkanGalat(galat: unknown, komponen?: string): void {
  if (!aktif) return
  const t: Tertunda = { galat, komponen }
  if (pelapor !== null) pelapor(t)
  else if (antrean.length < BATAS_ANTREAN) antrean.push(t)
}

/** Starts reporting after the first paint when a DSN was built in. */
export function mulaiPelaporan(dsn: string | undefined): void {
  if (dsn === undefined || dsn === '' || aktif) return
  aktif = true
  const onError = (e: ErrorEvent) => laporkanGalat(e.error ?? e.message)
  // Not a view transition the browser aborted (./batasTransisi), as the SDK drops it later.
  const onRejection = (e: PromiseRejectionEvent) => {
    if (!adalahBatasTransisi(e.reason)) laporkanGalat(e.reason)
  }
  window.addEventListener('error', onError)
  window.addEventListener('unhandledrejection', onRejection)

  const muat = () => {
    import('../sentry')
      .then((m) => {
        // From here the SDK's own handlers see window errors.
        window.removeEventListener('error', onError)
        window.removeEventListener('unhandledrejection', onRejection)
        pelapor = m.mulaiSentry(dsn, antrean.splice(0))
      })
      .catch(() => {
        // The chunk could not load (an old tab after a deploy, storage cleared): no reports.
      })
  }
  const saatSenggang = () => {
    const idle = window.requestIdleCallback ?? ((fn: () => void) => window.setTimeout(fn, 2000))
    idle(muat, { timeout: 10_000 })
  }
  // After the first contentful paint (a hidden tab may never paint: then after 10 s).
  const setelahPaint = () => {
    let selesai = false
    const lanjut = () => {
      if (selesai) return
      selesai = true
      pengamat?.disconnect()
      saatSenggang()
    }
    let pengamat: PerformanceObserver | null = null
    try {
      pengamat = new PerformanceObserver((daftar) => {
        if (daftar.getEntriesByName('first-contentful-paint').length > 0) lanjut()
      })
      pengamat.observe({ type: 'paint', buffered: true })
    } catch {
      pengamat = null
    }
    window.setTimeout(lanjut, 10_000)
  }
  if (document.readyState === 'complete') setelahPaint()
  else window.addEventListener('load', setelahPaint, { once: true })
}
